<template>
  <!-- Popup de conversas (plano-google-chat.md, fase 1): últimas conversas + busca de agentes e do diretório da ISBET. -->
  <span class="icp">
    <button v-if="canUse" ref="btn" type="button" class="icp-btn" :title="totalUnread ? `Conversas (${totalUnread} não lidas)` : 'Conversas'" aria-haspopup="dialog" :aria-expanded="open" @click.stop="toggle">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
      </svg>
      <span v-if="totalUnread" class="icp-badge">{{ totalUnread > 99 ? '99+' : totalUnread }}</span>
    </button>

    <Teleport to="body">
      <div v-if="open" ref="pop" class="icp-pop" role="dialog" aria-label="Conversas" :style="popStyle" @click.stop>
        <!-- Arraste pelo cabeçalho; redimensione pelo canto inferior direito. Posição e tamanho ficam neste navegador. -->
        <div class="icp-head" :class="{ dragging }" title="Arraste para mudar de lugar" @pointerdown="startDrag">
          <b>Conversas</b>
          <span class="icp-keys" title="Atalho">Ctrl+Shift+C</span>
          <button v-if="moved" type="button" class="icp-reset" title="Voltar ao lugar e tamanho padrão" aria-label="Voltar ao lugar padrão" @click="resetGeometry">↺</button>
          <button type="button" class="icp-min" title="Minimizar (fica um ícone no canto inferior esquerdo)" aria-label="Minimizar" @click="minimize">–</button>
          <button type="button" class="icp-x" title="Fechar" aria-label="Fechar" @click="close">×</button>
        </div>
        <p v-if="example" class="icp-example" role="note">
          <b>Exemplo.</b> O Google Workspace ainda não está configurado (Admin › Google Workspace). Os nomes e mensagens abaixo são
          fictícios, só pra mostrar como o popup fica.
        </p>
        <input id="icp-search" ref="searchEl" v-model="query" class="icp-search" type="search" placeholder="Buscar pessoa por nome ou e-mail…" autocomplete="off" />
        <p v-if="exampleNote" class="icp-note icp-example-note">{{ exampleNote }}</p>

        <div class="icp-body">
          <template v-if="!q">
            <div class="icp-sec">Últimas conversas</div>
            <p v-if="loadingRecent && !shownRecent.length" class="icp-note">Carregando…</p>
            <p v-else-if="!shownRecent.length" class="icp-note">Nenhuma conversa ainda. Escolha um agente abaixo.</p>
            <button v-for="r in shownRecent" :key="r.peerAgentId" type="button" class="icp-row" @click="openChat(r.peerAgentId)" @mouseenter="showCard(r.peerAgentId, $event)" @mouseleave="hideCard">
              <span class="icp-av" :style="{ background: avatarColor(r.peerName) }">
                <img v-if="r.peerAvatarUrl" :src="toMediaUrl(r.peerAvatarUrl)!" alt="" />
                <template v-else>{{ initials(r.peerName) }}</template>
                <i v-if="availabilityOf(r.peerAgentId)" class="icp-dot" :class="`is-${availabilityOf(r.peerAgentId)}`"></i>
              </span>
              <span class="icp-main">
                <span class="icp-line"><b>{{ r.peerName }}</b><small>{{ when(r.lastAt) }}</small></span>
                <span class="icp-prev" :class="{ unread: r.unread }">{{ r.fromMe ? 'Você: ' : '' }}{{ r.lastBody }}</span>
              </span>
              <span v-if="r.unread" class="icp-count">{{ r.unread }}</span>
            </button>

            <div class="icp-sec icp-sec--agents">
              <span>Agentes</span>
              <span class="icp-sec-count"><i class="icp-mini-dot"></i>{{ onlineCount }} online · {{ allAgents.length }} no total</span>
            </div>
            <p v-if="!allAgents.length" class="icp-note">{{ loadingAgents ? 'Carregando…' : 'Nenhum outro agente.' }}</p>
            <button v-for="a in allAgents" :key="a.id" type="button" class="icp-row" :class="{ 'is-offline': a.availability === 'offline' }" @click="openChat(a.id)" @mouseenter="showCard(a.id, $event)" @mouseleave="hideCard">
              <span class="icp-av" :style="{ background: avatarColor(a.name) }">
                <img v-if="a.avatarUrl" :src="toMediaUrl(a.avatarUrl)!" alt="" />
                <template v-else>{{ initials(a.name) }}</template>
                <i class="icp-dot" :class="`is-${a.availability}`"></i>
              </span>
              <span class="icp-main">
                <span class="icp-line"><b>{{ a.name }}</b><small :class="`icp-st-${a.availability}`">{{ AVAILABILITY[a.availability] }}</small></span>
                <span class="icp-prev">{{ a.role || a.email }}</span>
              </span>
              <span v-if="!example && unreadByAgent[a.id]" class="icp-count">{{ unreadByAgent[a.id] }}</span>
            </button>
          </template>

          <template v-else>
            <div class="icp-sec">Agentes do WootDash</div>
            <p v-if="!agentMatches.length" class="icp-note">Nenhum agente com "{{ query.trim() }}".</p>
            <button v-for="a in agentMatches" :key="a.id" type="button" class="icp-row" @click="openChat(a.id)" @mouseenter="showCard(a.id, $event)" @mouseleave="hideCard">
              <span class="icp-av" :style="{ background: avatarColor(a.name) }">
                <img v-if="a.avatarUrl" :src="toMediaUrl(a.avatarUrl)!" alt="" />
                <template v-else>{{ initials(a.name) }}</template>
                <i class="icp-dot" :class="`is-${a.availability}`"></i>
              </span>
              <span class="icp-main">
                <span class="icp-line"><b>{{ a.name }}</b><small>{{ AVAILABILITY[a.availability] }}</small></span>
                <span class="icp-prev">{{ a.email }}</span>
              </span>
              <span v-if="!example && unreadByAgent[a.id]" class="icp-count">{{ unreadByAgent[a.id] }}</span>
            </button>

            <template v-if="directory.enabled || example">
              <div class="icp-sec">Diretório da ISBET</div>
              <p v-if="q.length < 2" class="icp-note">Digite pelo menos 2 letras.</p>
              <p v-else-if="loadingDirectory && !example" class="icp-note">Procurando no diretório…</p>
              <p v-else-if="directory.problem && !example" class="icp-note icp-warn">{{ directory.problem }}</p>
              <p v-else-if="!directoryOthers.length" class="icp-note">Ninguém mais no diretório com "{{ query.trim() }}".</p>
              <div v-for="p in directoryOthers" :key="p.email" class="icp-row icp-row--static" :title="p.agentId ? '' : 'Esta pessoa não usa o WootDash'">
                <span class="icp-av" :style="{ background: avatarColor(p.name) }">
                  <img v-if="p.photoUrl" :src="p.photoUrl" alt="" referrerpolicy="no-referrer" />
                  <template v-else>{{ initials(p.name) }}</template>
                </span>
                <span class="icp-main">
                  <span class="icp-line"><b>{{ p.name }}</b></span>
                  <span class="icp-prev">{{ p.email }}</span>
                </span>
                <button v-if="p.agentId" type="button" class="icp-open" @click="openChat(p.agentId)">Conversar</button>
                <span v-else class="icp-tag">não está no WootDash</span>
              </div>
            </template>
          </template>
        </div>
      </div>

      <!-- Cartão de detalhes do agente (passar o mouse), como o da aba Agentes do Dashboard. -->
      <div v-if="open && card" class="icp-card" :style="card.style" role="tooltip">
        <div class="icp-card-head">
          <span class="icp-card-av" :style="{ background: avatarColor(card.agent.name) }">
            <img v-if="card.agent.avatarUrl" :src="toMediaUrl(card.agent.avatarUrl)!" alt="" />
            <template v-else>{{ initials(card.agent.name) }}</template>
            <i class="icp-dot" :class="`is-${card.agent.availability}`"></i>
          </span>
          <span class="icp-card-id">
            <b>{{ card.agent.name }}</b>
            <small>{{ card.agent.role || '—' }}</small>
          </span>
        </div>
        <div class="icp-card-body">
          <span v-if="card.agent.email" class="icp-card-row">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
            <span class="icp-card-text">{{ card.agent.email }}</span>
          </span>
          <span class="icp-card-row">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><circle cx="12" cy="12" r="10"/></svg>
            <span class="icp-card-badge" :class="`is-${card.agent.availability}`">{{ AVAILABILITY_LABEL[card.agent.availability] }}</span>
          </span>
          <span v-if="card.stats" class="icp-card-row">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="3" y="3" width="18" height="18" rx="2"/><line x1="9" y1="9" x2="9" y2="15"/></svg>
            {{ card.stats.open }} aberta(s) · {{ card.stats.pending }} pendente(s)
          </span>
          <span v-if="card.stats?.open" class="icp-card-row icp-card-atend">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3 18v-6a9 9 0 0 1 18 0v6"/><path d="M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3zM3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3z"/></svg>
            em atendimento
          </span>
          <span v-if="!example && unreadByAgent[card.agent.id]" class="icp-card-row icp-card-unread">{{ unreadByAgent[card.agent.id] }} mensagem(ns) não lida(s)</span>
        </div>
      </div>
    </Teleport>
  </span>
</template>

<script setup lang="ts">
// Ícone de balão na barra superior: últimas conversas do chat interno (com não lidas) e busca de agentes e do diretório
// da ISBET (G3). Selecionar abre o chat interno do WootDash (a gaveta de sempre). Tudo atualiza por SignalR.
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useStore } from 'vuex'
import { usePermission } from '@/composables/usePermission'
import { useAgentChatCp, type ChatAgentCp } from '@/composables/useAgentChatCp'
import { useWootDashLiveData } from '@/composables/useWootDashLiveData'
import { useChatRailMode } from '@/composables/useChatRailMode'
import { onInternalMessage } from '@/composables/useConversationRealtime'
import { avatarColor, initials } from '@/composables/useHelpers'
import { toMediaUrl } from '@/utils/media'
import { exampleData, shortcutAction, shouldOpenExample, useInternalChatPopup } from '@/composables/useInternalChatPopup'
import { getMyGoogleChat, listRecentInternalConversations, searchDirectory, type DirectorySearchResult, type RecentInternalConversation } from '@/services/wootdashGoogleChat'

const AVAILABILITY: Record<string, string> = { online: 'online', busy: 'ocupado', offline: 'offline' }
const AVAILABILITY_LABEL: Record<string, string> = { online: 'Online', busy: 'Ocupado', offline: 'Offline' }

// Modo exemplo (Ctrl+Shift+C sem o Google Workspace configurado): dados fictícios, só pra aprovar o visual.
const SAMPLE = exampleData()

const store = useStore()
const { can } = usePermission()
const token = computed<string>(() => store.getters.authUser?.wootdash_token ?? '')
const canUse = computed(() => can('agentChat.view'))
const { agents, unreadByAgent, totalUnread, panelOpen, loadingAgents, loadAgents, openThread } = useAgentChatCp()
const myId = computed<string>(() => store.getters.authUser?.wootdash_agent_id ?? '')
// Conversas ativas do Dashboard (abertas/pendentes) — alimentam o cartão de detalhes, como na aba Agentes.
const { conversations: liveConversations, scopeReady: liveReady } = useWootDashLiveData()
const { mode: railMode } = useChatRailMode()

const { shortcutRequests, state: popupState, exampleMode: example } = useInternalChatPopup()
const open = computed(() => popupState.value === 'open')
const query = ref('')
const q = computed(() => query.value.trim())
const recent = ref<RecentInternalConversation[]>([])
const loadingRecent = ref(false)
const directory = ref<DirectorySearchResult>({ enabled: true, people: [], problem: null })
const loadingDirectory = ref(false)
const btn = ref<HTMLElement | null>(null)
const pop = ref<HTMLElement | null>(null)
const searchEl = ref<HTMLInputElement | null>(null)
const popStyle = ref<Record<string, string>>({})

const exampleNote = ref<string | null>(null)
const allAgents = computed(() => (example.value ? SAMPLE.agents : agents.value))
const shownRecent = computed(() => (example.value ? SAMPLE.recent : recent.value))

const agentMatches = computed(() => {
  const text = q.value.toLowerCase()
  return allAgents.value.filter(a => a.name.toLowerCase().includes(text) || a.email.toLowerCase().includes(text)).slice(0, 20)
})
/** Do diretório, só quem não apareceu na lista de agentes (o agente já está acima). */
const directoryOthers = computed(() => {
  const shown = new Set(agentMatches.value.map(a => a.id.toLowerCase()))
  const text = q.value.toLowerCase()
  const people = example.value
    ? (q.value.length < 2 ? [] : SAMPLE.directory.filter(p => p.name.toLowerCase().includes(text) || p.email.toLowerCase().includes(text)))
    : directory.value.people
  return people.filter(p => !p.agentId || !shown.has(p.agentId.toLowerCase()))
})

const onlineCount = computed(() => allAgents.value.filter(a => a.availability === 'online').length)

// ── Cartão de detalhes ao passar o mouse: ao lado da janela (à esquerda; à direita se não couber) ──
const CARD_W = 250
const card = ref<{ agent: ChatAgentCp; stats: { open: number; pending: number } | null; style: Record<string, string> } | null>(null)

function statsOf(agentId: string) {
  if (example.value || !liveReady.value) return null
  let open = 0
  let pending = 0
  for (const c of liveConversations.value) {
    if (c.assignee?.id !== agentId) continue
    if (c.status === 'open') open++
    else if (c.status === 'pending') pending++
  }
  return { open, pending }
}

function showCard(agentId: string, e: MouseEvent) {
  const agent = allAgents.value.find(a => a.id.toLowerCase() === agentId.toLowerCase())
  const box = pop.value?.getBoundingClientRect()
  if (!agent || !box) { card.value = null; return }
  const row = (e.currentTarget as HTMLElement).getBoundingClientRect()
  const left = box.left - CARD_W - 10 >= 8 ? box.left - CARD_W - 10 : Math.min(box.right + 10, window.innerWidth - CARD_W - 8)
  const top = Math.max(8, Math.min(row.top - 8, window.innerHeight - 220))
  card.value = { agent, stats: statsOf(agent.id), style: { left: `${left}px`, top: `${top}px`, width: `${CARD_W}px` } }
}
function hideCard() { card.value = null }

function availabilityOf(agentId: string) {
  return allAgents.value.find(a => a.id.toLowerCase() === agentId.toLowerCase())?.availability ?? null
}

async function loadRecent() {
  loadingRecent.value = true
  recent.value = await listRecentInternalConversations(token.value)
  loadingRecent.value = false
}

let searchTimer: ReturnType<typeof setTimeout> | null = null
let searchSeq = 0
watch(q, text => {
  if (searchTimer) clearTimeout(searchTimer)
  exampleNote.value = null
  if (example.value) return
  if (text.length < 2) { directory.value = { ...directory.value, people: [], problem: null }; return }
  searchTimer = setTimeout(async () => {
    const mine = ++searchSeq
    loadingDirectory.value = true
    const result = await searchDirectory(token.value, text)
    if (mine !== searchSeq) return
    loadingDirectory.value = false
    directory.value = result
  }, 300)
})

// ── Posição e tamanho: o agente arrasta pelo cabeçalho e redimensiona pelo canto; fica guardado neste navegador ──
interface Geometry { left: number; top: number; width: number; height: number | null }
const GEOMETRY_KEY = 'wootdash.conversas-popup'
const MIN_WIDTH = 300
const MIN_HEIGHT = 260
const EDGE = 8
const geometry = ref<Geometry | null>(loadGeometry())
const moved = computed(() => geometry.value !== null)
const dragging = ref(false)

function loadGeometry(): Geometry | null {
  try {
    const saved = JSON.parse(localStorage.getItem(GEOMETRY_KEY) ?? 'null') as Geometry | null
    return saved && Number.isFinite(saved.left) && Number.isFinite(saved.top) && Number.isFinite(saved.width) ? saved : null
  } catch { return null }
}

function saveGeometry() {
  try {
    if (geometry.value) localStorage.setItem(GEOMETRY_KEY, JSON.stringify(geometry.value))
    else localStorage.removeItem(GEOMETRY_KEY)
  } catch { /* modo privado: vale só nesta visita */ }
}

/** Mantém o popup inteiro dentro da janela (a janela pode ter diminuído desde a última vez). */
function clamp(g: Geometry): Geometry {
  const width = Math.min(Math.max(g.width, MIN_WIDTH), window.innerWidth - EDGE * 2)
  const height = g.height === null ? null : Math.min(Math.max(g.height, MIN_HEIGHT), window.innerHeight - EDGE * 2)
  const left = Math.min(Math.max(g.left, EDGE), window.innerWidth - width - EDGE)
  const top = Math.min(Math.max(g.top, EDGE), window.innerHeight - (height ?? MIN_HEIGHT) - EDGE)
  return { left, top, width, height }
}

function place() {
  if (geometry.value) {
    const g = clamp(geometry.value)
    popStyle.value = { top: `${g.top}px`, left: `${g.left}px`, width: `${g.width}px`, ...(g.height === null ? { maxHeight: 'min(560px, 80vh)' } : { height: `${g.height}px`, maxHeight: 'none' }) }
    return
  }
  const width = Math.min(360, window.innerWidth - 16)
  // Sem o botão (agente sem o chat interno, no modo exemplo): canto superior direito.
  const r = btn.value?.getBoundingClientRect() ?? { bottom: 56, right: window.innerWidth - 16 }
  // Tamanho padrão: até 560px de altura; redimensionado, vale o que o agente escolheu.
  popStyle.value = { top: `${r.bottom + 8}px`, left: `${Math.max(8, Math.min(window.innerWidth - width - 8, r.right - width))}px`, width: `${width}px`, maxHeight: 'min(560px, 80vh)' }
}

function currentGeometry(): Geometry | null {
  const rect = pop.value?.getBoundingClientRect()
  return rect ? { left: rect.left, top: rect.top, width: rect.width, height: geometry.value?.height ?? null } : null
}

function startDrag(e: PointerEvent) {
  if (e.button !== 0 || (e.target as HTMLElement).closest('button')) return
  const start = currentGeometry()
  if (!start) return
  e.preventDefault()
  dragging.value = true
  const head = e.currentTarget as HTMLElement
  head.setPointerCapture(e.pointerId)
  const dx = e.clientX - start.left
  const dy = e.clientY - start.top
  const onMove = (ev: PointerEvent) => {
    geometry.value = clamp({ ...start, left: ev.clientX - dx, top: ev.clientY - dy })
    place()
  }
  const onUp = () => {
    dragging.value = false
    head.removeEventListener('pointermove', onMove)
    head.removeEventListener('pointerup', onUp)
    head.removeEventListener('pointercancel', onUp)
    saveGeometry()
  }
  head.addEventListener('pointermove', onMove)
  head.addEventListener('pointerup', onUp)
  head.addEventListener('pointercancel', onUp)
}

// Redimensionar: o canto do próprio popup (CSS resize). Guarda o tamanho quando a pessoa solta.
let resizeObserver: ResizeObserver | null = null
let resizeTimer: ReturnType<typeof setTimeout> | null = null
let lastSize = ''
function watchResize(el: HTMLElement | null) {
  resizeObserver?.disconnect()
  if (!el || typeof ResizeObserver === 'undefined') return
  lastSize = `${el.offsetWidth}x${el.offsetHeight}`
  resizeObserver = new ResizeObserver(() => {
    const size = `${el.offsetWidth}x${el.offsetHeight}`
    if (size === lastSize || dragging.value) return
    lastSize = size
    if (resizeTimer) clearTimeout(resizeTimer)
    resizeTimer = setTimeout(() => {
      const rect = el.getBoundingClientRect()
      geometry.value = clamp({ left: rect.left, top: rect.top, width: rect.width, height: rect.height })
      saveGeometry()
    }, 200)
  })
  resizeObserver.observe(el)
}
watch(pop, el => watchResize(el))

function resetGeometry() {
  geometry.value = null
  saveGeometry()
  place()
  void nextTick(() => { lastSize = pop.value ? `${pop.value.offsetWidth}x${pop.value.offsetHeight}` : '' })
}

/** Abre do zero (o watch de popupState carrega tudo). */
function show(asExample: boolean) {
  example.value = asExample
  popupState.value = 'open'
}

/** Só o × fecha. */
function close() {
  popupState.value = 'closed'
  card.value = null
}

/** Vira o ícone de chat no canto inferior esquerdo (InternalChatBubble). */
function minimize() {
  popupState.value = 'minimized'
  card.value = null
}

// Abriu — do zero, do ícone minimizado ou pelo "voltar" do chat com um agente: mesmo lugar e tamanho, recentes e status
// dos agentes atualizados (uma leitura ao abrir, sem polling).
watch(popupState, (now, before) => {
  if (now !== 'open') return
  if (before === 'closed') { query.value = ''; exampleNote.value = null }
  place()
  if (!example.value) {
    void loadRecent()
    if (token.value && myId.value) void loadAgents(token.value, myId.value)
  }
  void nextTick(() => searchEl.value?.focus())
})

/** Botão da barra: fechado abre; minimizado volta; aberto minimiza (clicar fora não fecha mais). */
function toggle() {
  if (popupState.value === 'open') { minimize(); return }
  if (popupState.value === 'minimized') { popupState.value = 'open'; return }
  show(false)
}

/** Lê se a ponte e o diretório do Google Workspace estão prontos (Admin › Google Workspace). */
async function workspaceStatus() {
  const [me, dir] = await Promise.all([getMyGoogleChat(token.value), searchDirectory(token.value, '')])
  directory.value = dir
  return { bridge: me.bridgeEnabled, directory: dir.enabled }
}

// Ctrl+Shift+C: fechado abre (sem o Google Workspace configurado — ou sem o chat interno —, como exemplo); aberto
// minimiza; minimizado volta. Nunca fecha: só o × fecha.
watch(shortcutRequests, async () => {
  const action = shortcutAction(popupState.value)
  if (action === 'minimize') { minimize(); return }
  if (action === 'restore') { popupState.value = 'open'; return }
  const status = await workspaceStatus()
  show(shouldOpenExample(canUse.value, status.bridge, status.directory))
})

async function openChat(agentId: string) {
  if (example.value) {
    exampleNote.value = 'Exemplo: com o Google Workspace configurado, isso abre a conversa com a pessoa no chat interno.'
    return
  }
  // A conversa abre na gaveta; o popup fica minimizado no canto, à mão pra próxima.
  minimize()
  query.value = ''
  railMode.value = 'agent'
  panelOpen.value = true
  await openThread(token.value, agentId)
}

// Mensagem nova com o popup aberto: a lista de recentes muda na hora.
let recentTimer: ReturnType<typeof setTimeout> | null = null
const stopRealtime = onInternalMessage(() => {
  if (!open.value) return
  if (recentTimer) clearTimeout(recentTimer)
  recentTimer = setTimeout(() => { void loadRecent() }, 250)
})

onMounted(() => { window.addEventListener('resize', place) })
onBeforeUnmount(() => { window.removeEventListener('resize', place); stopRealtime(); resizeObserver?.disconnect() })

function when(iso: string) {
  const d = new Date(iso)
  const today = new Date()
  return d.toDateString() === today.toDateString()
    ? d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
    : d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })
}
</script>

<style scoped>
.icp { position: relative; display: inline-flex; }
.icp-btn {
  position: relative; display: flex; align-items: center; justify-content: center; width: 36px; height: 36px; flex-shrink: 0;
  background: rgba(255,255,255,.09); border: 1px solid rgba(255,255,255,.16); border-radius: 50%; color: rgba(255,255,255,.75); cursor: pointer;
}
.icp-btn:hover { background: rgba(255,255,255,.18); color: #fff; }
.icp-btn svg { width: 16px; height: 16px; }
.icp-badge { position: absolute; top: -4px; right: -4px; min-width: 17px; height: 17px; padding: 0 4px; border-radius: 9px; background: #e11d48; color: #fff; font-size: 10px; font-weight: 700; display: flex; align-items: center; justify-content: center; box-sizing: border-box; }
.icp-pop {
  position: fixed; z-index: 3900; background: var(--white, #fff); color: var(--ink-80, #1f2937); border: 1px solid var(--ink-10, #e5e7eb); border-radius: 12px;
  box-shadow: 0 14px 36px rgba(15, 23, 42, .2); display: flex; flex-direction: column; max-height: calc(100vh - 16px); font-size: 13px; overflow: hidden;
  /* Canto inferior direito: redimensionar (o tamanho fica guardado). */
  resize: both; min-width: 300px; min-height: 260px; max-width: calc(100vw - 16px);
}
.icp-head { display: flex; align-items: center; justify-content: space-between; padding: 12px 14px 6px; font-size: 14px; cursor: grab; user-select: none; touch-action: none; }
.icp-head.dragging { cursor: grabbing; }
.icp-min { border: none; background: none; color: var(--ink-40, #9ca3af); font-size: 18px; line-height: 1; cursor: pointer; padding: 0 6px; font-weight: 700; }
.icp-min:hover { color: var(--ink-80, #1f2937); }
.icp-reset { border: none; background: none; color: var(--ink-40, #9ca3af); font-size: 15px; line-height: 1; cursor: pointer; padding: 0 6px; }
.icp-reset:hover { color: var(--ink-80, #1f2937); }
.icp-head b { flex: 1; }
.icp-keys { font-size: 10.5px; color: var(--ink-40, #9ca3af); border: 1px solid var(--ink-10, #e5e7eb); border-radius: 4px; padding: 1px 5px; margin-right: 6px; }
.icp-example { margin: 0 12px 8px; padding: 8px 10px; border-radius: 8px; background: #fffbeb; border: 1px solid #fde68a; color: #92400e; font-size: 12px; line-height: 1.45; }
.icp-example-note { color: #92400e; padding-top: 0; }
.icp-x { border: none; background: none; font-size: 20px; line-height: 1; color: var(--ink-40, #9ca3af); cursor: pointer; }
.icp-search { margin: 0 12px 8px; padding: 8px 10px; border: 1px solid var(--ink-10, #e5e7eb); border-radius: 8px; font: inherit; font-size: 13px; background: var(--white, #fff); color: inherit; }
.icp-search:focus { outline: none; border-color: #2563eb; }
.icp-body { overflow-y: auto; padding: 0 6px 8px; flex: 1; min-height: 0; }
.icp-sec { font-size: 10.5px; font-weight: 700; text-transform: uppercase; letter-spacing: .06em; color: var(--ink-40, #9ca3af); padding: 8px 8px 4px; }
.icp-note { margin: 0; padding: 4px 8px 8px; color: var(--ink-40, #6b7280); font-size: 12.5px; }
.icp-warn { color: #b45309; }
.icp-row {
  display: grid; grid-template-columns: 34px minmax(0, 1fr) auto; gap: 10px; align-items: center; width: 100%; text-align: left; border: none; background: none;
  padding: 7px 8px; border-radius: 8px; cursor: pointer; font: inherit; color: inherit; box-sizing: border-box;
}
.icp-row:hover, .icp-row:focus-visible { background: var(--ink-05, #f3f4f6); outline: none; }
.icp-row--static { cursor: default; }
.icp-av { position: relative; width: 34px; height: 34px; border-radius: 50%; color: #fff; font-size: 12px; font-weight: 700; display: flex; align-items: center; justify-content: center; overflow: visible; }
.icp-av img { width: 100%; height: 100%; border-radius: 50%; object-fit: cover; }
.icp-dot { position: absolute; right: -1px; bottom: -1px; width: 10px; height: 10px; border-radius: 50%; border: 2px solid var(--white, #fff); background: #9ca3af; }
.icp-dot.is-online { background: #16a34a; }
.icp-dot.is-busy { background: #f59e0b; }
.icp-main { display: flex; flex-direction: column; min-width: 0; }
.icp-line { display: flex; justify-content: space-between; gap: 8px; }
.icp-line b { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.icp-line small { color: var(--ink-40, #9ca3af); font-size: 11px; white-space: nowrap; }
.icp-prev { color: var(--ink-50, #6b7280); font-size: 12px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.icp-prev.unread { color: var(--ink-80, #1f2937); font-weight: 600; }
.icp-count { min-width: 18px; height: 18px; border-radius: 9px; padding: 0 5px; background: #2563eb; color: #fff; font-size: 10.5px; font-weight: 700; display: flex; align-items: center; justify-content: center; box-sizing: border-box; }
.icp-open { border: 1px solid #2563eb; background: transparent; color: #2563eb; border-radius: 6px; padding: 3px 8px; font: inherit; font-size: 11.5px; font-weight: 600; cursor: pointer; }
.icp-tag { font-size: 10.5px; color: var(--ink-40, #9ca3af); white-space: nowrap; }
.icp-sec--agents { display: flex; align-items: center; justify-content: space-between; gap: 8px; margin-top: 6px; border-top: 1px solid var(--ink-10, #e5e7eb); padding-top: 10px; }
.icp-sec-count { display: inline-flex; align-items: center; gap: 5px; font-weight: 600; text-transform: none; letter-spacing: 0; font-size: 11px; }
.icp-mini-dot { width: 7px; height: 7px; border-radius: 50%; background: #16a34a; }
.icp-row.is-offline .icp-av { opacity: .55; }
.icp-row.is-offline b { color: var(--ink-50, #6b7280); font-weight: 600; }
.icp-line small.icp-st-online { color: #16a34a; font-weight: 600; }
.icp-line small.icp-st-busy { color: #d97706; font-weight: 600; }
.icp-card {
  position: fixed; z-index: 3950; pointer-events: none; box-sizing: border-box; padding: 12px 14px; border-radius: 14px;
  background: rgba(2, 22, 39, .97); color: #fff; border: 1px solid rgba(255,255,255,.13); box-shadow: 0 8px 32px rgba(0,0,0,.45); font-size: 12px;
}
.icp-card-head { display: flex; align-items: center; gap: 10px; }
.icp-card-av { position: relative; width: 40px; height: 40px; border-radius: 50%; flex-shrink: 0; display: flex; align-items: center; justify-content: center; font-size: 13px; font-weight: 800; color: #fff; }
.icp-card-av img { width: 100%; height: 100%; border-radius: 50%; object-fit: cover; }
.icp-card-av .icp-dot { border-color: #021627; }
.icp-card-id { display: flex; flex-direction: column; min-width: 0; }
.icp-card-id b { font-size: 13px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.icp-card-id small { color: rgba(255,255,255,.6); font-size: 11px; }
.icp-card-body { display: flex; flex-direction: column; gap: 7px; margin-top: 10px; padding-top: 10px; border-top: 1px solid rgba(255,255,255,.09); }
.icp-card-row { display: flex; align-items: center; gap: 7px; color: rgba(255,255,255,.82); min-width: 0; }
.icp-card-row svg { width: 13px; height: 13px; flex-shrink: 0; color: rgba(255,255,255,.45); }
.icp-card-text { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.icp-card-badge { padding: 1px 8px; border-radius: 999px; font-size: 10.5px; font-weight: 700; background: rgba(156,163,175,.25); color: #e5e7eb; }
.icp-card-badge.is-online { background: rgba(22,163,74,.25); color: #4ade80; }
.icp-card-badge.is-busy { background: rgba(245,158,11,.25); color: #fbbf24; }
.icp-card-atend { color: #60a5fa; font-weight: 600; }
.icp-card-atend svg { color: #60a5fa; }
.icp-card-unread { color: #fda4af; font-weight: 600; }
</style>
